import * as React from 'react'
import {Collapse, Typography, Switch, FormControlLabel, Chip, Box} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER} from '../../../styles/foundation/colors'

function WeightDiscrepancyInstructions() {
  const [instructionsOpen, setInstructionsOpen] = React.useState(false)
  const [isHindi, setIsHindi] = React.useState(false)

  const toggleInstructions = () => {
    setInstructionsOpen(!instructionsOpen)
  }

  const toggleLanguage = () => {
    setIsHindi(!isHindi)
  }

  const englishInstructions = `To ensure faster and more successful resolution of weight discrepancy claims, please prioritize uploading a video over static images. A complete end-to-end packing video, clearly showing how the SKU was packed into its final outer packaging, is considered the most reliable form of evidence—both by Prozo and courier partners. Videos are significantly harder to fabricate and offer higher credibility. Since volumetric weight is determined based on the outer packaging, this must be clearly captured in the video. Claims supported by such videos have a ~100% approval rate.

If uploading a video is not possible, you may submit images. However, please note that the approval rate with images is lower (~50%). For volumetric disputes, upload an image of the fully packed shipment with a visible measuring scale. For dead weight disputes, submit an image of the shipment on a weighing scale showing the reading clearly. Claims submitted without proper outer packaging visuals or only individual SKU images will be rejected.

Until a weight discrepancy history is established on Prozo's shipping platform for your brand and SKUs, we rely solely on the proof provided by you (merchant) to contest claims with courier partners. Once sufficient shipment history is built, Prozo uses a combination of your submitted evidence and historical trends to evaluate and dispute discrepancies more effectively.`

  const hindiInstructions = `वजन से संबंधित दावों के तेज़ और सफल समाधान के लिए कृपया वीडियो को प्राथमिकता दें। एक संपूर्ण एंड-टू-एंड पैकिंग वीडियो, जिसमें यह स्पष्ट रूप से दिखे कि SKU को अंतिम आउटर पैकेजिंग में कैसे पैक किया गया, सबसे भरोसेमंद प्रमाण माना जाता है — Prozo और कूरियर पार्टनर्स दोनों के द्वारा। वीडियो में हेरफेर की संभावना बहुत कम होती है, और कूरियर कंपनियां वॉल्यूमेट्रिक वेट का निर्धारण आउटर बॉक्स के आधार पर करती हैं, इसलिए यह स्पष्ट रूप से वीडियो में दिखना चाहिए। ऐसे वीडियो के साथ सबमिट किए गए दावों की लगभग 100% अप्रूवल रेट होती है।

यदि वीडियो अपलोड करना संभव न हो, तो आप इमेजेस दे सकते हैं, लेकिन कृपया ध्यान दें कि इनकी अप्रूवल रेट लगभग 50% होती है। वॉल्यूमेट्रिक वेट विवाद के लिए, पूरी तरह से पैक किए गए शिपमेंट की इमेज दें जिसमें मापने का स्केल स्पष्ट रूप से दिख रहा हो। डेड वेट के लिए, शिपमेंट को वेटिंग मशीन पर रखकर उसका वजन स्पष्ट रूप से दिखाई देने वाली इमेज दें। केवल SKU की इमेज या बिना आउटर पैकेजिंग के इमेज पर आधारित दावों को रिजेक्ट कर दिया जाएगा।

जब तक आपके ब्रांड और SKU के लिए Prozo के शिपिंग प्लेटफॉर्म पर वेट डिस्क्रेपेंसी का कोई इतिहास नहीं बनता, तब तक हम आपके द्वारा उपलब्ध कराए गए प्रमाण के आधार पर ही कूरियर पार्टनर के साथ क्लेम्स को चुनौती देते हैं। एक बार जब पर्याप्त डेटा इतिहास बन जाता है, तो Prozo आपकी सबमिट की गई सामग्री और ऐतिहासिक डेटा दोनों का उपयोग कर बेहतर निर्णय लेता है।`

  return (
    <Box sx={{mb: 1.5}}>
      <Box sx={{bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden'}}>
        {/* Header Section */}
        <Box
          onClick={toggleInstructions}
          sx={{
            cursor: 'pointer',
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: instructionsOpen ? '#f8fafc' : 'transparent',
            borderBottom: instructionsOpen ? `1px solid ${BORDER}` : 'none',
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1}}>
            <InfoOutlinedIcon
              sx={{
                color: '#3b82f6',
                fontSize: 16,
                p: 0.3,
                bgcolor: '#eff6ff',
                borderRadius: '50%',
                width: 26,
                height: 26,
              }}
            />
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: NAVY}}>
              Weight Discrepancy Guidelines
            </Typography>
            <Chip
              label='Important'
              size='small'
              sx={{
                bgcolor: '#10b981',
                color: '#fff',
                fontWeight: 600,
                fontSize: 11,
                height: 20,
                '& .MuiChip-label': {px: 0.8},
              }}
            />
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, color: '#6b7280'}}>
            {instructionsOpen ? (
              <ExpandLessIcon sx={{fontSize: 18}} />
            ) : (
              <>
                <Typography sx={{fontSize: 12, color: SUBTLE, fontWeight: 500}}>View more</Typography>
                <ExpandMoreIcon sx={{fontSize: 18}} />
              </>
            )}
          </Box>
        </Box>

        {/* Collapsible Content */}
        <Collapse in={instructionsOpen}>
          <Box sx={{p: 2, bgcolor: '#fafbfc'}}>
            {/* Language Toggle */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                p: 1.5,
                bgcolor: '#fff',
                borderRadius: '8px',
                border: `1px solid ${BORDER}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  color: NAVY,
                  fontSize: SIZE['2xs'],
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                Language
              </Typography>
              <FormControlLabel
                sx={{m: 0, '& .MuiSwitch-root': {mr: 1}}}
                control={<Switch checked={isHindi} onChange={toggleLanguage} size='small' />}
                label={
                  <Typography sx={{color: NAVY, fontWeight: 600, fontSize: SIZE['2xs']}}>
                    {isHindi ? 'हिंदी' : 'English'}
                  </Typography>
                }
              />
            </Box>

            {/* Instructions Content */}
            <Box sx={{mb: 2, p: 2, bgcolor: '#fff', borderRadius: '8px', border: `1px solid ${BORDER}`}}>
              <Typography
                sx={{
                  lineHeight: 1.5,
                  color: '#374151',
                  whiteSpace: 'pre-line',
                  fontSize: '0.82rem',
                  fontFamily: isHindi
                    ? '"Noto Sans Devanagari", "Roboto", sans-serif'
                    : '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontWeight: 400,
                }}
              >
                {isHindi ? hindiInstructions : englishInstructions}
              </Typography>
            </Box>

            {/* Pro Tip Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                  flexShrink: 0,
                }}
              >
                💡
              </Box>
              <Typography sx={{color: '#fff', fontWeight: 600, fontSize: SIZE['2xs']}}>
                Pro Tip:{' '}
                {isHindi
                  ? 'वीडियो सबमिशन के लिए सर्वोत्तम परिणाम प्राप्त करें'
                  : 'Get the best results with video submissions'}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default WeightDiscrepancyInstructions
